import {
  Component,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
} from '@angular/core';
import { TransactionsFacade } from '../../abstraction/transactions-facade';
import { Observable, Subject, from, BehaviorSubject } from 'rxjs';
import { Transaction } from '../../model/transaction';
import { MatDialog } from '@angular/material';
import { TransactionCreationModalComponent } from '../transaction-creation-modal/transaction-creation-modal.component';
import { TransactionDeleteModalComponent } from '../transaction-delete-modal/transaction-delete-modal.component';

import { PeriodicTransactionCreationModalComponent } from '../pt-creation-modal/periodic-transaction-creation-modal.component';
import { takeUntil } from 'rxjs/operators';
import { Ptransaction } from '../../model/ptransaction';
import {
  TABLEHEADERSPUNCTUALTRANSACTIONS,
  TABLEHEADERSPTRANSACTIONSHEADERS,
  ACTIONSPUNCTUAL,
  ACTIONSPERIODIC,
} from './constants';
import { MachineLearningModalComponent } from '../machine-learning-modal/machine-learning-modal.component';

@Component({
  selector: 'makaw-transactions',
  templateUrl: './transactions.component.html',
})
export class TransactionsComponent implements OnInit, OnDestroy {
  @Output() balanceOutput = new EventEmitter<any>();

  public loading = false;
  public ready = false;
  public openGoalsModal = new BehaviorSubject<boolean>(false);
  public requestSavingTarget$ = this.openGoalsModal.asObservable();
  public transactionsLoaded = false;
  public isUpdating$: Observable<boolean>;
  public transaction$: Observable<Transaction[]>;
  public ptransaction$: Observable<Ptransaction[]>;
  public prediction$: Observable<any[]>;
  private cachedTransactions: Transaction[] = [];
  public transactions: Transaction[] = [];
  public ptransactions: Ptransaction[] = [];
  public predictions: number;
  private destroyed$ = new Subject();

  public tableHeaders = TABLEHEADERSPUNCTUALTRANSACTIONS;
  public tableHeadersPtransactions = TABLEHEADERSPTRANSACTIONSHEADERS;
  public actionsPast = ACTIONSPUNCTUAL;
  public actionsPeriodic = ACTIONSPERIODIC;

  public titleTableTransaction = 'transactions.past';
  public titleTablePeriodicTransaction = 'transactions.periodic';
  private showFutureTransactions = false;

  constructor(
    private transactionsFacade: TransactionsFacade,
    public dialog: MatDialog
  ) {}

  ngOnInit() {
    this.loading = true;
    this.transactionsFacade.loadData();
    this.transaction$ = this.transactionsFacade.getTransactions$();
    this.ptransaction$ = this.transactionsFacade.getPTransactions$();
    this.transactionsLoaded = true;

    this.transaction$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((transactions) => {
        this.transactions = transactions;
        if (transactions.length > 0) this.ready = true;
        this.loading = false;
      });

    this.ptransaction$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((ptransactions) => {
        this.ptransactions = ptransactions;
      });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.transactionsFacade.stopSubscription();
  }

  deleteTransaction(transaction: Transaction) {
    if (this.showFutureTransactions) this.exitSimulationOfFutureTransactions();
    const data = JSON.parse(JSON.stringify(transaction));
    data.msg = 'transactions.deletePunctual';
    data.action = 'Delete';
    const dialogRef = this.dialog.open(TransactionDeleteModalComponent, {
      width: '500px',
      data: data,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result.event === 'Delete') {
        console.log(`${result.data}`);
        this.transactionsFacade.deleteTransaction(result.data);
      }
    });
  }

  updateTransaction(transaction: Transaction) {
    if (this.showFutureTransactions) this.exitSimulationOfFutureTransactions();
    const data = JSON.parse(JSON.stringify(transaction));
    data.msg = 'Edit transaction';
    data.action = 'Edit';
    data.loaded = true;

    const dialogRef = this.dialog.open(TransactionCreationModalComponent, {
      width: '500px',
      data: data,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result.event === 'Edit') {
        console.log(result.data);

        this.transactionsFacade.updateTransaction(result.data);
      }
    });
  }

  createTransaction() {
    if (this.showFutureTransactions) this.exitSimulationOfFutureTransactions();
    const newtransaction: any = {};
    newtransaction.action = 'Create';
    newtransaction.msg = 'transactions.addPunctual';
    newtransaction.loaded = false;
    const dialogRef = this.dialog.open(TransactionCreationModalComponent, {
      width: '500px',
      data: newtransaction,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result.event === 'Create')
        this.transactionsFacade.addTransaction(result.data);
    });
  }

  createPeriodicTransaction() {
    if (this.showFutureTransactions) this.exitSimulationOfFutureTransactions();
    const newtransaction: any = {};
    newtransaction.action = 'Create';
    newtransaction.msg = 'transactions.addPeriodic';
    newtransaction.loaded = false;
    const dialogRef = this.dialog.open(
      PeriodicTransactionCreationModalComponent,
      {
        width: '500px',
        data: newtransaction,
      }
    );

    dialogRef.afterClosed().subscribe((result) => {
      if (result.event === 'Create')
        this.transactionsFacade.addPTransaction(result.data);
    });
  }

  updatePTransaction(ptransaction: Ptransaction) {
    if (this.showFutureTransactions) this.exitSimulationOfFutureTransactions();
    const data = JSON.parse(JSON.stringify(ptransaction));

    data.msg = 'transactions.editPtransaction';
    data.action = 'Edit';
    data.loaded = true;

    const dialogRef = this.dialog.open(
      PeriodicTransactionCreationModalComponent,
      {
        width: '500px',
        data: data,
      }
    );

    dialogRef.afterClosed().subscribe((result) => {
      if (result.event === 'Edit')
        this.transactionsFacade.editPTransaction(
          result.data,
          this.transactions
        );
    });
  }

  requestPrediction() {
    if (this.showFutureTransactions) this.exitSimulationOfFutureTransactions();
    const dataSentToModal: any = {};
    dataSentToModal.action = 'ml.request';
    dataSentToModal.msg = 'transactions.calculatePredictions';
    dataSentToModal.loaded = false;
    const dialogRef = this.dialog.open(MachineLearningModalComponent, {
      width: '500px',
      data: dataSentToModal,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result.event === 'ml.request') this.getPredictions(result.data);
    });
  }

  requestTarget() {
    this.openGoalsModal.next(true);
  }

  closeModal() {
    this.openGoalsModal.next(false);
  }

  deletePTransaction(ptransaction: Transaction) {
    if (this.showFutureTransactions) this.exitSimulationOfFutureTransactions();
    const data = JSON.parse(JSON.stringify(ptransaction));
    data.msg = 'transactions.deletePeriodic';
    data.action = 'Delete';
    const dialogRef = this.dialog.open(TransactionDeleteModalComponent, {
      width: '500px',
      data: data,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result.event === 'Delete') {
        console.log(`${result.data}`);
        this.transactionsFacade.clearPeriodicTransaction(
          result.data,
          this.transactions
        );
      }
    });
  }

  simulateFutureTransactions() {
    this.cachedTransactions = JSON.parse(JSON.stringify(this.transactions));
    this.transactionsFacade.simulateFutureTransactions(this.ptransactions);
    this.showFutureTransactions = true;
  }
  exitSimulationOfFutureTransactions() {
    this.transactionsFacade.exitSimulationOfFutureTransactions(
      this.cachedTransactions
    );
    this.showFutureTransactions = false;
  }
  getPredictions(requestData: number[]) {
    this.loading = true;
    debugger;
    this.transactionsFacade.calculatePredictions(requestData);
    this.prediction$ = this.transactionsFacade.getPrediction$();
    this.prediction$.pipe(takeUntil(this.destroyed$)).subscribe(
      (predictions: any) => {
        debugger;
        if (predictions.length !== 0) {
          this.predictions = +predictions.predictions;
          console.log('PREDICTIONS:: ', this.predictions);
          this.loading = false;
        }
      },
      (error) => console.log(error)
    );
  }

  setBalance(balance: number) {
    this.balanceOutput.emit(balance);
  }
}
